import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  // Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  // Badge,
  MenuItem,
} from '@mui/material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ReusableDataGrid, CustomDrawer, ActionsMenu, type ActionMenuItem } from '@/components/common';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RootState, AppDispatch } from '@/store/types';
import { useAuth } from '@/hooks';
import {
  fetchStatusEnquiries,
  createStatusEnquiry,
  updateStatusEnquiry,
  deleteStatusEnquiry,
} from '@/store/slices/statusEnquirySlice';
import { fetchUsers } from '@/store/slices/userSlice';
import { StatusEnquiryFormData } from '@/services/statusEnquiry/statusEnquiryService';
import moment from 'moment';

const validationSchema = Yup.object({
  employeeId: Yup.string().required('Employee is required'),
  message: Yup.string().required('Message is required').min(5, 'Message must be at least 5 characters'),
  // isActive: Yup.boolean().required('Status is required'),
});

const initialFormData: StatusEnquiryFormData = {
  employeeId: '',
  message: '',
  // isActive: true,
};

export const ManageStatusEnquiry: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { statusEnquiries, statusEnquiriesLoading, isLoading, pagination } = useSelector(
    (state: RootState) => state.statusEnquiry
  );
  const { users } = useSelector((state: RootState) => state.user);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState<string>('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | ''>('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Check if user can edit/delete based on role
  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const isEmployee = user?.role === 'employee';

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (editingId) {
          await dispatch(updateStatusEnquiry({
            id: editingId,
            data: values
          }));
        } else {
          await dispatch(createStatusEnquiry(values));
        }
        handleCloseDrawer();
        loadData();
      } catch (error) {
        console.error('Error saving status enquiry:', error);
      }
    },
  });

  // Load users for dropdown
  useEffect(() => {
    dispatch(fetchUsers({ limit: 1000, isActive: true }));
  }, [dispatch]);

  const loadData = useCallback(() => {
    const params: any = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search: searchTerm || undefined,
      isActive: isActiveFilter !== '' ? isActiveFilter : undefined,
    };

    // If employee, automatically filter by their ID
    if (isEmployee && user?._id) {
      params.employeeId = user._id;
    } else if (selectedEmployeeFilter) {
      // If admin/manager and selected an employee, filter by that employee
      params.employeeId = selectedEmployeeFilter;
    }

    dispatch(fetchStatusEnquiries(params));
  }, [dispatch, paginationModel, searchTerm, isActiveFilter, selectedEmployeeFilter, isEmployee, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenDrawer = (statusEnquiry?: any) => {
    if (statusEnquiry) {
      formik.setValues({
        employeeId: statusEnquiry.employeeId._id,
        message: statusEnquiry.message,
        // isActive: statusEnquiry.isActive,
      });
      setEditingId(statusEnquiry._id);
    } else {
      formik.resetForm();
      // If employee, pre-fill their ID
      if (isEmployee && user?._id) {
        formik.setFieldValue('employeeId', user._id);
      }
      setEditingId(null);
    }
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    formik.resetForm();
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteStatusEnquiry(id));
      setDeleteConfirmId(null);
      loadData();
    } catch (error) {
      console.error('Error deleting status enquiry:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setIsActiveFilter('');
    if (!isEmployee) {
      setSelectedEmployeeFilter('');
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (isActiveFilter !== '') count++;
    if (!isEmployee && selectedEmployeeFilter) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // const getStatusColor = (isActive: boolean) => {
  //   return isActive ? 'success' : 'default';
  // };

  const columns: GridColDef[] = [
    {
      field: 'employeeId',
      headerName: 'Employee',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.value?.empId}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.value?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.value?.empId}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {moment(params.value).format('DD/MM/YYYY hh:mm A')}
        </Typography>
      ),
    },
    // {
    //   field: 'isActive',
    //   headerName: 'Status',
    //   flex: 0.6,
    //   minWidth: 100,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value ? 'Active' : 'Inactive'}
    //       color={getStatusColor(params.value) as any}
    //       size="small"
    //     />
    //   ),
    // },
  ];

  // Add actions column only for admin and manager
  if (canEdit) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const actions: ActionMenuItem[] = [
          {
            id: 'edit',
            title: 'Edit',
            onClick: () => handleOpenDrawer(params.row),
            disabled: isLoading,
          },
          {
            id: 'delete',
            title: 'Delete',
            onClick: () => setDeleteConfirmId(params.row._id),
            disabled: isLoading,
          },
        ];

        return (
          <ActionsMenu
            actions={actions}
            disabled={isLoading}
          />
        );
      },
    });
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h5" component="h5" flexGrow={1}>
            Status Enquiries
          </Typography>
        </Box>
        <Divider />
      </Box>

      {/* Filter and Action Buttons */}
      <Grid container spacing={2} alignItems="center" justifyContent="flex-end" sx={{ mb: 3 }}>
        {!isEmployee && (
          <Grid size={{ xs: 12, sm: 'auto' }} sx={{ minWidth: { xs: '100%', sm: 250 } }}>
            <TextField
              select
              label="Filter by Employee"
              variant="outlined"
              size="small"
              fullWidth
              value={selectedEmployeeFilter}
              onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
            >
              <MenuItem value="">All Employees</MenuItem>
              {users?.map((employee) => (
                <MenuItem key={employee._id} value={employee._id}>
                  {employee.name} ({employee.empId})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        {/* <Grid size="auto">
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>
        </Grid> */}
        {canEdit && (
          <Grid size="auto">
            <Button
              variant="contained"
              onClick={() => handleOpenDrawer()}
              disabled={isLoading}
            >
              Add Status Enquiry
            </Button>
          </Grid>
        )}
      </Grid>

      <ReusableDataGrid
        rows={statusEnquiries || []}
        columns={columns}
        getRowId={(row) => row?._id}
        loading={statusEnquiriesLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationData={pagination ? { totalRecords: pagination.totalItems } : undefined}
        noRowsMessage="No status enquiries found"
        noRowsActionLabel={canEdit ? "Create Status Enquiry" : undefined}
        onNoRowsAction={canEdit ? () => handleOpenDrawer() : undefined}
      />

      {/* Create/Edit Drawer */}
      {canEdit && (
        <CustomDrawer
          open={openDrawer}
          onClose={handleCloseDrawer}
          title={editingId ? 'Edit Status Enquiry' : 'Create Status Enquiry'}
          subtitle={editingId ? 'Update status enquiry details' : 'Add a new status enquiry'}
          width={'100%'}
          disableBackdropClick={true}
        >
          <Box sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  select
                  label="Employee"
                  name="employeeId"
                  required
                  value={formik.values.employeeId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                  helperText={formik.touched.employeeId && formik.errors.employeeId}
                  disabled={isEmployee}
                >
                  <MenuItem value="">Select Employee</MenuItem>
                  {users?.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      {employee.name} ({employee.empId})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  required
                  multiline
                  rows={4}
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.message && Boolean(formik.errors.message)}
                  helperText={formik.touched.message && formik.errors.message}
                  placeholder="Enter your status enquiry message..."
                />
              </Grid>
              {/* <Grid size={{xs: 12}}>
                <TextField
                  fullWidth
                  select
                  name="isActive"
                  label="Status"
                  required
                  value={formik.values.isActive}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.isActive && Boolean(formik.errors.isActive)}
                  helperText={formik.touched.isActive && formik.errors.isActive}
                  variant="outlined"
                  slotProps={{
                    select: {
                      native: true,
                    },
                  }}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </TextField>
              </Grid> */}
            </Grid>
          </Box>
          <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCloseDrawer} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={(e:any) => formik.handleSubmit(e)}
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : editingId ? 'Update' : 'Create'}
            </Button>
          </Box>
        </CustomDrawer>
      )}

      {/* Delete Confirmation Dialog */}
      {canEdit && (
        <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this status enquiry? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
            <Button
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              color="error"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Filter Drawer */}
      <CustomDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filter Status Enquiries"
        width={'100%'}
        anchor="right"
        actions={
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
            startIcon={<FilterIcon />}
            fullWidth
          >
            Clear Filters
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by message..."
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />

          {activeFilterCount > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
              </Typography>
            </Box>
          )}
        </Box>
      </CustomDrawer>
    </Box>
  );
};
